import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import * as moment from "moment-timezone";

import { StreamProvider } from "@teamrota/shiftstreamlib";
import { RotaButton, RotaDropdown } from "@teamrota/rota-design";

import Modal from "~/src/components/modal";
import Tabs from "~/src/containers/modals/components/tabs";
import { formatDateMonthTimeShort } from "~/src/utils/formatting";
import { COLORS } from "~/src/styles/config.style";

import { IconDiv } from "~/src/components/payroll/style";
import IconLink from "~/src/containers/payroll/components/IconLink";

import {
  ActionHelpText,
  ActionRow,
  Content,
  DetailsSurround,
  DetailsTable,
  Footer,
  MetadataDiv,
  Subtitle,
  Title,
} from "./styles";

import { shiftStreamClient } from "../../../graphql/client";
import { Provider } from "../../../components/provider";
import { ReasonPanel, StreamPanel } from "../../../components/stream";
import { useAccounts } from "../../../graphql/use-accounts";
import { getEmailStatus } from "../../../utils";

import StreamInfoModal from "../../streams/modal";

import { useEmail } from "../graphql/use-email";

import { EMAIL_ALGO } from "../graphql/mutation/email-algo";
import { EMAIL_CANCEL } from "../graphql/mutation/email-cancel";
import { EMAIL_CLOSE } from "../graphql/mutation/email-close";
import { EMAIL_EXPIRE } from "../graphql/mutation/email-expire";
import { EMAIL_EXTEND_EXPIRY } from "../graphql/mutation/email-extend-expiry";
import { EMAIL_RERUN } from "../graphql/mutation/email-rerun";
import { EMAIL_STOP } from "../graphql/mutation/email-stop";
import { CancelModal } from "../../../components/cancel";
import { ResultModal } from "../../../components/result";

interface EmailInfoModalProps {
  emailId: any | null;
  onClose: () => void;
}

const EmailInfoModal: React.FC<EmailInfoModalProps> = ({
  emailId,
  onClose,
}) => {
  if (!emailId) {
    return null;
  }

  const { email } = useEmail(parseInt(emailId));
  const { accounts } = useAccounts();

  if (!email) {
    return null;
  }

  // locate account for inbox
  let account = null;
  if (accounts) {
    account = accounts.find(
      (a: any) =>
        a.url.toLowerCase().trim() ===
          email.mailboxAddress.toLowerCase().trim() &&
        a.provider === StreamProvider.EMAIL,
    );
  }

  if (!account) {
    return null;
  }

  const modalTabs: Array<{ name: string; content: React.ReactNode }> = [
    {
      name: "Overview",
      content: <OverviewTab email={email} />,
    },
    {
      name: "Contents",
      content: <ContentsTab email={email} />,
    },
    {
      name: "Replies",
      content: <RepliesTab email={email} />,
    },
    {
      name: "Streams",
      content: <StreamsTab email={email} />,
    },
    {
      name: "Metadata",
      content: <MetadataTab email={email} />,
    },
  ];

  return (
    <Modal
      isOpen={Boolean(email)}
      isLoading={false}
      isOverlay={false}
      isLightOverlay={false}
      hasBtn={false}
      padding={0}
      background="white"
      onClose={onClose}
    >
      <Content>
        <Title>Email #{email.id}</Title>

        <Subtitle>
          <Provider account={account} />
        </Subtitle>

        <Tabs
          className="stream-info-tabs"
          defaultTab="Overview"
          tabs={modalTabs as any}
        />
      </Content>
    </Modal>
  );
};

const OverviewTab = ({ email }: { email: any }) => {
  const [resultModal, setResultModal] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);

  const [algo, { loading: isAlgoLoading }] = useMutation(EMAIL_ALGO, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const handleAlgo = async (id: any, isAlgo: boolean) => {
    try {
      const result = await algo({ variables: { id, algo: isAlgo } });
      if (result?.data?.emailAlgo) {
        setResultModal(result.data.emailAlgo);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [cancel, { loading: isCancelLoading }] = useMutation(EMAIL_CANCEL, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const handleCancel = async (id: any) => {
    try {
      const result = await cancel({ variables: { id } });
      if (result?.data?.emailCancel) {
        setResultModal(result.data.emailCancel);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [close, { loading: isCloseLoading }] = useMutation(EMAIL_CLOSE, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const handleClose = async (id: any) => {
    try {
      const result = await close({ variables: { id } });
      if (result?.data?.emailClose) {
        setResultModal(result.data.emailClose);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [expire, { loading: isExpireLoading }] = useMutation(EMAIL_EXPIRE, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const handleExpire = async (id: any) => {
    try {
      const result = await expire({ variables: { id } });
      if (result?.data?.emailExpire) {
        setResultModal(result.data.emailExpire);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [extendExpiry, { loading: isExtendExpiryLoading }] = useMutation(
    EMAIL_EXTEND_EXPIRY,
    {
      client: shiftStreamClient,
      refetchQueries: ["getEmail", "getEmails"],
    },
  );

  const handleExtendExpiry = async (id: any, hours: number) => {
    try {
      const result = await extendExpiry({ variables: { id, hours } });
      if (result?.data?.emailExtendExpiry) {
        setResultModal(result.data.emailExtendExpiry);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [reRun, { loading: isReRunLoading }] = useMutation(EMAIL_RERUN, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const handleReRun = async (id: any) => {
    try {
      const result = await reRun({ variables: { id } });
      if (result?.data?.emailReRun) {
        setResultModal(result.data.emailReRun);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [stop, { loading: isStopLoading }] = useMutation(EMAIL_STOP, {
    client: shiftStreamClient,
    refetchQueries: ["getEmail", "getEmails"],
  });

  const handleStop = async (id: any, stopped: boolean) => {
    try {
      const result = await stop({ variables: { id, stopped: !stopped } });
      if (result?.data?.emailStop) {
        setResultModal(result.data.emailStop);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [confirmingCancelId, setConfirmingCancelId] = React.useState<
    any | null
  >(null);
  const [extendHours, setExtendHours] = useState("24");

  const id = parseInt(email.id);

  const isExpired =
    email.isExpiredManually || moment(email.expiresAt).isBefore();

  const isAlgo = email.streams.find((stream: any) => !stream.noAlgo);

  return (
    <>
      <DetailsSurround>
        <DetailsTable>
          <tr>
            <th>ID</th>
            <td>#{email.id}</td>
          </tr>
          <tr>
            <th>From</th>
            <td>{email.sender}</td>
          </tr>
          <tr>
            <th>To</th>
            <td>{email.mailboxAddress}</td>
          </tr>
          <tr>
            <th>Subject</th>
            <td>{email.subject}</td>
          </tr>
          <tr>
            <th>Received</th>
            <td>
              {email.receivedAt
                ? formatDateMonthTimeShort(email.receivedAt)
                : "—"}
            </td>
          </tr>
          <tr>
            <th>Expiry</th>
            <td>
              {isExpired ? (
                <span style={{ color: COLORS.RED }}>
                  {formatDateMonthTimeShort(email.expiresAt)}
                  {email.isExpiredManually && " (brought forward manually)"}
                </span>
              ) : (
                <span>{formatDateMonthTimeShort(email.expiresAt)}</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Status</th>
            <td>{getEmailStatus(email)}</td>
          </tr>
        </DetailsTable>

        <Footer>
          <ActionRow>
            <RotaButton
              onClick={() => handleStop(id, email.stopped)}
              disabled={isExpired}
              size="large"
              variant={email.stopped ? "outlined" : "contained"}
              isLoading={isStopLoading}
            >
              {email.stopped ? "Resume Replies" : "Stop Replies"}
            </RotaButton>
            <ActionHelpText>
              <b>Temporarily stops all replies</b> from being sent for this
              email. Algorithm continues as usual.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => !isExpired && handleReRun(id)}
              disabled={isExpired}
              size="large"
              isLoading={isReRunLoading}
            >
              Re-Run Algo
            </RotaButton>
            <ActionHelpText>
              <b>Schedules the algorithm</b> on all streams to fill slots with
              any available candidates.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => handleAlgo(id, !isAlgo)}
              size="large"
              variant={isAlgo ? "contained" : "outlined"}
              disabled={isExpired}
              isLoading={isAlgoLoading}
            >
              {isAlgo ? "Stop Algo" : "Resume Algo"}
            </RotaButton>
            <ActionHelpText>
              <b>Stops any further algorithm runs</b> on all streams for this
              email, even if slots become available.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <RotaButton
                onClick={() => handleExtendExpiry(id, parseInt(extendHours))}
                size="small"
                disabled={isExpired || !email.expiresAt}
                isLoading={isExtendExpiryLoading}
                style={{
                  whiteSpace: "nowrap",
                  width: "100px",
                  fontSize: "16px",
                }}
              >
                Extend
              </RotaButton>
              <select
                value={extendHours}
                onChange={(e) => setExtendHours(e.target.value)}
                style={{
                  height: "36px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  padding: "0 8px",
                  fontSize: "14px",
                  width: "70px",
                }}
              >
                <option value="24">24 h</option>
                <option value="48">48 h</option>
                <option value="72">72 h</option>
              </select>
            </div>
            <ActionHelpText>
              <b>Extends the expiry time</b> by the selected number of hours.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => handleExpire(id)}
              size="large"
              variant="contained"
              disabled={isExpired}
              isLoading={isExpireLoading}
            >
              {isExpired ? "Expired" : "Expire Now"}
            </RotaButton>
            <ActionHelpText>
              <b>Brings expiry forward to now</b>, which will send the expiry
              email and close any unfilled slots.
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => handleClose(id)}
              size="large"
              disabled={isExpired}
              isLoading={isCloseLoading}
            >
              Close Email
            </RotaButton>
            <ActionHelpText>
              <b>Closes this email</b>, removing unfilled slots, expiry will
              still occur at the normal time. <br />
            </ActionHelpText>
          </ActionRow>

          <ActionRow>
            <RotaButton
              onClick={() => setConfirmingCancelId(id)}
              size="large"
              isLoading={isCancelLoading}
            >
              Cancel All
            </RotaButton>
            <ActionHelpText>
              <b>Cancels this email's streams</b>, and associated shifts and
              bookings. Candidates will be notified.
            </ActionHelpText>
          </ActionRow>
        </Footer>
      </DetailsSurround>

      {resultModal && (
        <ResultModal
          message={resultModal.message ?? null}
          isError={resultModal.isError ?? false}
          onDismiss={() => setResultModal(null)}
        />
      )}

      {confirmingCancelId && (
        <CancelModal
          streams={email.streams}
          isLoading={isCancelLoading}
          onProceed={async () => {
            setConfirmingCancelId(null);
            handleCancel(confirmingCancelId);
          }}
          onClose={() => setConfirmingCancelId(null)}
        />
      )}
    </>
  );
};

const ContentsTab = ({ email }: { email: any }) => {
  if (email.state === "SKIPPED") {
    return (
      <>
        <br />
        <Subtitle>Email is {getEmailStatus(email)}</Subtitle>
      </>
    );
  }

  return (
    <>
      <DetailsSurround>
        <DetailsTable>
          <tr>
            <th>ID</th>
            <td>#{email.id}</td>
          </tr>
          <tr>
            <th>From</th>
            <td>{email.sender}</td>
          </tr>
          <tr>
            <th>To</th>
            <td>{email.mailboxAddress}</td>
          </tr>
          <tr>
            <th>Subject</th>
            <td>{email.subject}</td>
          </tr>
          <tr>
            <th>Received</th>
            <td>
              {email.receivedAt
                ? formatDateMonthTimeShort(email.receivedAt)
                : "—"}
            </td>
          </tr>
        </DetailsTable>

        <iframe
          style={{ width: "47.8vw", height: "40vh" }}
          src={email.preview}
          sandbox=""
          referrerPolicy="no-referrer"
          loading="lazy"
        />
      </DetailsSurround>

      <div></div>
    </>
  );
};

const RepliesTab = ({ email }: { email: any }) => {
  const [expanded, setExpanded] = React.useState<number | null>(null);

  if (email.replies.length === 0) {
    return (
      <>
        <br />
        <Subtitle>No replies sent</Subtitle>
      </>
    );
  }

  return (
    <DetailsSurround>
      <p style={{ textAlign: "right" }}>
        <a href={email.dashboardUrl} target="_blank" rel="noopener noreferrer">
          Open Client Dashboard
        </a>
        <a
          href={email.dashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          &nbsp; &#x1F517;
        </a>
      </p>

      <DetailsTable>
        <thead>
          <tr>
            <th style={{ width: "4vw" }}>ID</th>
            <th>Template</th>
            <th>Sent At</th>
            <th style={{ width: "3.5vw" }}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {email.replies.map((reply: any) => {
            return (
              <>
                <tr key={reply.id}>
                  <td>#{reply.id}</td>
                  <td>{reply.template}</td>
                  <td>
                    {reply.sentAt
                      ? formatDateMonthTimeShort(reply.sentAt)
                      : "Pending"}
                  </td>
                  <td>
                    <IconDiv>
                      <IconLink
                        name={"VISIBLE"}
                        size={25}
                        isButton
                        color={
                          expanded === reply.id
                            ? COLORS.WARM_GREY
                            : COLORS.MAIN_ORANGE
                        }
                        tooltip="Expand"
                        onClick={() =>
                          setExpanded(expanded === reply.id ? null : reply.id)
                        }
                      />
                    </IconDiv>
                  </td>
                </tr>
                {expanded === reply.id && (
                  <tr>
                    <td colSpan={4}>
                      <iframe
                        style={{ width: "46.5vw", height: "30vh" }}
                        src={reply.preview}
                        sandbox=""
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </DetailsTable>
    </DetailsSurround>
  );
};

const METADATA_OPTIONS = [
  { value: "request", label: "Request" },
  { value: "response", label: "Response" },
];

const MetadataTab = ({ email }: { email: any }) => {
  const [selectedMetadata, setSelectedMetadata] = React.useState(
    METADATA_OPTIONS[0].value,
  );

  const metadataToShow =
    selectedMetadata === "request"
      ? email.metadata
      : email.parsed.map((p: any) => ({ ...p, __typename: undefined }));

  return (
    <>
      <div style={{ width: 300 }}>
        <RotaDropdown
          id="metadata-dropdown"
          label="Metadata View"
          errorMessage=""
          width="100%"
          value={selectedMetadata}
          placeholder="select metadata"
          options={METADATA_OPTIONS}
          onChange={(option) => setSelectedMetadata(option)}
        />
      </div>

      <MetadataDiv>{JSON.stringify(metadataToShow, null, 2)}</MetadataDiv>
    </>
  );
};

const StreamsTab = ({ email }: { email: any }) => {
  const [selectedStreamId, setSelectedStreamId] = React.useState<any | null>(
    null,
  );

  const noStreams =
    (!email.scrapeReasons && !email.streams) ||
    (email.streams.length === 0 && email.scrapeReasons.length === 0);

  return (
    <div style={{ height: "60vh", overflowY: "scroll" }}>
      {noStreams && (
        <>
          <br />
          <Subtitle>No streams for this email</Subtitle>
        </>
      )}

      {email.scrapeReasons &&
        email.scrapeReasons.map((reason: string) => (
          <ReasonPanel reason={reason} showMenu={true} />
        ))}

      {email.streams &&
        email.streams.map((stream: any) => (
          <StreamPanel
            stream={stream}
            showMenu={true}
            onClick={() => setSelectedStreamId(stream.id)}
          />
        ))}

      <StreamInfoModal
        streamId={selectedStreamId}
        onClose={() => setSelectedStreamId(null)}
      />
    </div>
  );
};

export default EmailInfoModal;
